import { alpha } from '@mui/material/styles';
import { figma } from './tokens/figma';

/**
 * THE CURRENT GLOBAL POP-UP RULES — every pop-up in the app follows these, and every
 * new or reworked dialog is built on them. Figma "pop-up guidline" `40156:67999`.
 *
 * One anatomy (`popup -slot` `39845:428758`: header `card-header-upd`, a content slot,
 * footer `card-footer`) in one of three size shells. A dialog picks a size; it does not
 * invent a width. These consumers read this file and nothing else restates its numbers:
 *
 *   - `mui.theme.tsx` → `MuiDialog`: the shell every dialog gets (backdrop, radius,
 *     shadow, distance from the screen edge, height cap);
 *   - `shared/ui/modal` → `<Modal size>`: the three sizes;
 *   - the import wizard's frame → `POPUP_MAX_EXTENT` for its judgement steps, the one
 *     exception to the sizes (`docs/DESIGN_SYSTEM.md` §Dialogs).
 *
 * Heights grow with the content (user ruling, 2026-10-06): small and medium start at
 * their drawn height and grow until the card is `POPUP_SCREEN_INSET` from the screen
 * edges, after which the body scrolls under a fixed header and footer. Large always
 * fills the screen height. All three frames are drawn on a 1440×900 screen.
 */

/** Gap between the card and every screen edge — all three frames sit 40px from the top and bottom. */
export const POPUP_SCREEN_INSET = 40;

/**
 * Corner radius of the `popup -slot` component (16). The sizing frames draw their placeholder
 * cards at 24; the component is what real pop-ups are built from, so it wins.
 */
export const POPUP_RADIUS = 16;

/** Figma `pop-up-blur`: `#2C3A42` at 40%. */
export const POPUP_BACKDROP = alpha(figma.grey.primary, 0.4);

/** The longest a card may be on either axis: the screen minus the inset on both sides. */
export const POPUP_MAX_EXTENT = `calc(100% - ${POPUP_SCREEN_INSET * 2}px)`;

export type PopupSize = 'small' | 'medium' | 'large';

export interface PopupSizeRule {
  /** Card width in px; narrower screens shrink it to `POPUP_MAX_EXTENT`. */
  width: number;
  /** Starting height in px, grown from as the content needs. Absent = always full height. */
  minHeight?: number;
}

export const POPUP_SIZES: Record<PopupSize, PopupSizeRule> = {
  /** `pop-up sizing / small` `40156:67991` — 600×400. Confirmations and short forms. */
  small: { width: 600, minHeight: 400 },
  /** `pop-up sizing / medium` `40156:67573` — 700 wide, from 500 up to full height. */
  medium: { width: 700, minHeight: 500 },
  /** `pop-up sizing / large` `40156:67995` — 1200 wide, always full height. Tables. */
  large: { width: 1200 },
};
